import React, { ReactElement, useContext, useEffect, useState } from "react";
import clsx from "clsx";

import moment from "moment";

import { ReactSVG } from "react-svg";
import DashboardContext from "src/context/dashboard-context";
import {
  formatNumber,
  getDashboardSumission,
  getGroupByData,
  getPortfolioStatus,
} from "src/utils/helper/functions";
import { Colors } from "src/utils/data/constant";
import { Status } from "src/models/enum";
import DoughnutChart from "./doughnut-chart";
import PortfolioDetailsPerformance from "./portfolio-details-performance.component";

type AccountPortfolioDetailsProps = {
  onClose: () => void;
};

const options = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
  },
};

const AccountPortfolioDetails: React.FC<AccountPortfolioDetailsProps> = ({
  onClose,
}: AccountPortfolioDetailsProps): ReactElement => {
  const {
    selectedPortfolio,
    assets,
    liabilities,
    selectedAccount,
    assetsTotal,
    liabilitiesTotal,
  }: any = useContext(DashboardContext);
  const [assetsAllocationData, setAssetsAllocationData] = useState<any[]>([]);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    prepareData();
  }, [assets, liabilities]);

  const prepareData = () => {
    const groupedData = getGroupByData(
      assets.filter(
        (asset: any) => asset.portfolioCode === +selectedPortfolio?.accountCode
      ),
      "productGroupName"
    );
    const values: any = [];
    setAssetsAllocationData(
      Object.entries(groupedData)?.map(([key, value]: any) => {
        const summission = (
          (getDashboardSumission(value, "totalPercentageFromPortfolio") /
            getDashboardSumission(assets, "totalPercentageFromPortfolio")) *
          100
        )?.toFixed(1);
        values.push(summission);
        return {
          label: key,
          value: summission,
        };
      })
    );

    setData({
      labels: Object.keys(groupedData),
      datasets: [
        {
          label: "",
          data: values,
          backgroundColor: Colors,
          borderColor: Colors,
          borderWidth: 1,
          borderJoinStyle: "round",
          // borderRadius: 10,
          // spacing: 3,
          // rotation: 10
        },
      ],
    });
  };

  return (
    <section className="w-full flex flex-col">
      <div className=" h-[31px] justify-between items-center inline-flex">
        <div className=" h-[27px] justify-start items-center gap-2.5 flex">
          <div
            onClick={onClose}
            className="cursor-pointer w-6 h-6 justify-center items-center flex"
          >
            <img
              src="/images/long-back-arrow.svg"
              width={24}
              height={24}
              alt="back"
            />
          </div>

          <div className=" text-[#2a2a2a] text-base lg:text-[22px] font-bold">
            {`Portfolio #`}
            {selectedPortfolio?.accountCode || "New"}
          </div>
        </div>
      </div>
      {selectedPortfolio?.status === "Blocked" && (
        <div className="self-stretch p-2 mt-5 bg-amber-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-400 inline-flex justify-start items-center gap-2">
          <ReactSVG src="/images/info-icon.svg" className="info w-5 h-5" />

          <div className="flex-1 justify-start text-neutral-600 text-xs font-medium font-['Montserrat']">
            This portfolio has been blocked due to{" "}
            {selectedPortfolio?.portfolioStatusReason}
          </div>
        </div>
      )}
      <div className="flex flex-col md:flex-row mt-5 p-4 bg-white rounded-2xl add-shadow border border-[#e3e3e3] justify-between items-start">
        <div className="w-full md:w-[49%] justify-start items-start gap-3 md:gap-5 mb-5 md:mb-0 flex">
          <div className="w-full flex-col justify-start items-start gap-3 md:gap-5 inline-flex">
            <div className=" self-stretch justify-start items-center inline-flex">
              <div className=" grow shrink basis-0 self-stretch opacity-90 text-[#535353] text-sm lg:text-base font-bold">
                Net asset ({selectedPortfolio?.portfolioCurrency})
              </div>
            </div>
            <div className=" self-stretch flex-col justify-between items-start flex">
              <div className=" self-stretch justify-between items-center inline-flex">
                {selectedPortfolio?.portfolioMarketAmount > 0 ? (
                  <div className=" justify-start items-center gap-1.5 flex">
                    <div className="">
                      <span className="text-[#002f6c] text-lg md:text-2xl lg:text-[32px] font-bold">
                        {formatNumber(
                          selectedPortfolio?.portfolioMarketAmount,
                          selectedPortfolio?.reportingCurrencyDcp
                        )}
                      </span>
                    </div>
                  </div>
                ) : (
                  "-"
                )}
              </div>
              <div className="w-full justify-between items-center inline-flex mt-3">
                <div className="grow flex-col justify-start items-start gap-1 md:gap-3 inline-flex">
                  <div className="TotalAssets self-stretch opacity-70 text-[#2a2a2a] text-xs md:text-sm font-medium">
                    Total assets
                  </div>
                  <div className="Frame1015 justify-start items-center gap-1 md:gap-3 inline-flex">
                    <div className="Frame1008 self-stretch justify-start items-center gap-1 flex">
                      <div className="000557Kwd">
                        <span className="text-[#40bd6e] text-sm md:text-base font-semibold">
                          {formatNumber(
                            assetsTotal as number,
                            selectedPortfolio?.reportingCurrencyDcp
                          )}{" "}
                        </span>
                        <span className="text-[#40bd6e] text-xs md:text-sm font-semibold">
                          {selectedPortfolio?.portfolioCurrency}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex grow">
                  <div className="flex w-px h-[17px] bg-[#b1b1b1] rounded-[11px]" />
                </div>
                <div className="grow flex-col justify-start items-start gap-1 md:gap-3 inline-flex">
                  <div className="TotalLiabilities self-stretch opacity-70 text-[#2a2a2a] text-xs md:text-sm font-medium">
                    Total liabilities
                  </div>
                  <div className="Frame1015 justify-start items-center gap-1 md:gap-3 inline-flex">
                    <div className="Frame1008 justify-start items-center gap-1 flex">
                      <div className="000557Kwd">
                        <span className="text-[#f5606e] text-sm md:text-base font-semibold">
                          {formatNumber(
                            Math.abs(liabilitiesTotal as number),
                            selectedPortfolio?.reportingCurrencyDcp
                          )}{" "}
                        </span>
                        <span className="text-[#f5606e] text-xs md:text-sm font-semibold">
                          {selectedPortfolio?.portfolioCurrency}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col md:flex-row items-start justify-between mt-3 gap-y-3">
        <div className=" h-full w-full md:w-[49%] p-4 bg-white rounded-2xl add-shadow border border-[#e3e3e3] flex-col justify-start items-start gap-3 inline-flex">
          <div className=" self-stretch grow shrink basis-0 flex-col justify-start items-start gap-5 flex">
            <div className=" self-stretch justify-start items-center gap-4 inline-flex">
              <div className=" grow shrink basis-0 self-stretch opacity-90 text-[#2a2a2a] text-sm md:text-base font-bold ">
                Portfolio details
              </div>
            </div>
            <div className=" self-stretch grow gap-5 mt-5 shrink basis-0 flex-col justify-between items-start flex">
              <div className=" w-full flex justify-between items-center gap-3">
                <div className=" text-[#535353] text-xs font-semibold ">
                  Portfolio number
                </div>
                <div className="flex items-center gap-2">
                  {selectedPortfolio?.onlineTradeSys ? (
                    <div className="bg-[#005BD2] p-1 rounded justify-start text-slate-100 text-xs font-semibold ">
                      Trading
                    </div>
                  ) : selectedPortfolio?.managementTypeCode === "DX" ||
                    selectedPortfolio?.managementTypeCode === "NX" ? (
                    <div className="bg-[#840B55] p-1 rounded justify-start text-slate-100 text-xs font-semibold ">
                      Kamco products
                    </div>
                  ) : (
                    <div className="bg-[#958748] p-1 rounded justify-start text-slate-100 text-xs font-semibold ">
                      Investment
                    </div>
                  )}
                  <div className=" text-right text-[#2a2a2a] text-xs font-semibold ">
                    {selectedPortfolio?.accountCode ||
                      `#${selectedAccount?.accountCode}-New`}
                  </div>
                </div>
              </div>
              <div className=" self-stretch justify-start items-center gap-4 inline-flex">
                <div className=" grow shrink basis-0 justify-start items-start gap-3 flex">
                  <div className=" text-[#535353] text-xs font-semibold ">
                    Portfolio status
                  </div>
                  <div className=" grow shrink basis-0 justify-end items-center gap-2 flex">
                    <div
                      className={clsx(
                        " w-2 h-2 rounded-full",
                        getPortfolioStatus(selectedPortfolio?.statusCode) ===
                          "Pending"
                          ? "bg-amber-500"
                          : getPortfolioStatus(
                              selectedPortfolio?.statusCode
                            ) === "Blocked" ||
                            getPortfolioStatus(
                              selectedPortfolio?.statusCode
                            ) === Status.Closed
                          ? "bg-[#f44336]"
                          : "bg-[#00b050]"
                      )}
                    />
                    <div className=" text-right text-[#2a2a2a] text-xs font-semibold ">
                      {getPortfolioStatus(
                        selectedPortfolio?.statusCode
                      )?.toLowerCase()}
                    </div>
                  </div>
                </div>
              </div>
              <div className=" self-stretch justify-start items-center gap-4 inline-flex">
                <div className=" justify-between w-full items-start gap-3 flex">
                  <div className=" text-[#535353] text-[11px] font-semibold ">
                    Portfolio type
                  </div>
                  <div className=" text-right text-[#2a2a2a] text-xs font-semibold ">
                    {selectedPortfolio?.managementType}
                  </div>
                </div>
              </div>
              <div className=" self-stretch justify-start items-center gap-4 inline-flex">
                <div className=" grow shrink basis-0 justify-start items-start gap-3 flex">
                  <div className=" text-[#535353] text-xs font-semibold ">
                    Portfolio open date
                  </div>
                  <div className=" grow shrink basis-0 justify-end items-center gap-2 flex">
                    <div className=" text-right text-[#2a2a2a] text-xs font-semibold ">
                      {selectedPortfolio?.openDate
                        ? moment(selectedPortfolio?.openDate)?.format(
                            "MMM DD, YYYY"
                          )
                        : "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex w-full md:w-[49%]">
          <PortfolioDetailsPerformance
            performanceData={selectedAccount?.performance || []}
            selectedPortfolio={selectedPortfolio}
            selectedAccount={selectedAccount}
          />
        </div>
      </div>
      <div className="w-full mt-3 p-4 bg-white rounded-2xl add-shadow border border-[#e3e3e3] flex-col justify-start items-start gap-5 flex">
        <div className=" self-stretch justify-start items-center gap-4 inline-flex">
          <div className=" grow shrink basis-0 opacity-90 text-[#2a2a2a] text-sm md:text-base font-bold">
            Portfolio summary
          </div>
        </div>
        {data?.labels?.length ? (
          <div className="flex flex-col md:flex-row items-start justify-between w-full gap-y-3">
            <div className=" flex-col w-full md:w-[45%] justify-center items-center gap-10 flex">
              <div className=" self-stretch flex-col justify-start items-center gap-5 flex">
                <div className=" self-stretch flex-col justify-center items-center gap-5 flex">
                  {assetsAllocationData
                    ?.filter((ele: any) => ele.value != 0)
                    ?.map((item: any, index: number) => (
                      <div
                        key={index}
                        className="Frame575950 self-stretch h-[15px] justify-between items-center inline-flex"
                      >
                        <div className=" justify-start items-center gap-1 flex">
                          <span
                            className={clsx(`w-2 h-2 rounded-full`)}
                            style={{ backgroundColor: Colors[index] }}
                          ></span>
                          <div className=" text-[#2a2a2a] text-xs font-semibold">
                            {item?.label}
                          </div>
                        </div>
                        <div className=" w-[38px] text-right text-[#2a2a2a] text-xs font-semibold">
                          {item?.value}%
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
            <div className="doughnut-chart flex items-center justify-center w-full">
              {data && <DoughnutChart data={data} options={options} />}
            </div>
          </div>
        ) : (
          <div className="flex flex-col flex-grow justify-center items-center w-full">
            <img
              src={"/images/empty-performance-icon.svg"}
              width={40}
              height={40}
              alt="Empty Performance Icon"
            />
            <p className="text-neutral-400 text-sm mt-3">
              You have no summary yet
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default AccountPortfolioDetails;
